import { type Connection, MarkerType, type HandleType, type Node } from "@xyflow/react";

import { reconnectableForEdgeType, type RuleEdge, type RuleEdgeType } from "../../types/edges";
import type { LoopNodeData, RuleNode } from "../../types/nodes";

// ─── Sentinel node IDs (stable constants, never stored) ─────────────────────
export const FOREACH_SENTINEL_ID = "__loop-forEach-sentinel__";
export const AFTER_LAST_SENTINEL_ID = "__loop-afterLast-sentinel__";
export const CONTINUE_SENTINEL_ID = "__loop-continue-sentinel__";
/** Synthetic Start → End "After Last" edge — not persisted. */
export const LOOP_START_TO_END_EDGE_ID = "__loop-start-to-end__";
/** Synthetic Loop Start `loop-start` → Continue `in` ("For Each" entry) when body empty — not persisted. */
export const LOOP_START_TO_CONTINUE_EDGE_ID = "__loop-start-to-continue__";
/** Synthetic Continue `out` → Loop Start `in` — not persisted. */
export const LOOP_CONTINUE_TO_START_EDGE_ID = "__loop-continue-to-start__";

// ─── Default positions in the sub-canvas ────────────────────────────────────
export const FOREACH_SENTINEL_POSITION = { x: 250, y: 80 };
export const AFTER_LAST_SENTINEL_POSITION = { x: 600, y: 500 };
/** Default under Loop Start (same width) so empty-body "For Each" edge is vertical. */
export const CONTINUE_SENTINEL_POSITION = { x: 250, y: 500 };

// ─── Navigation stack ────────────────────────────────────────────────────────
export type LoopStackEntry = {
  loopNodeId: string;
  loopName: string;
  /** Shown in breadcrumbs as "Name (Variable)" / "Name (Table)". */
  loopType?: "variable" | "table";
};

/** Same resolution as the loop node card (explicit `data.loopType`, else infer from table labels). */
export function resolveLoopIterateType(data: LoopNodeData): "variable" | "table" {
	const tableLabel =
    data.databaseTableName ||
    data.screenTableName ||
    data.collectionName ||
    data.tableName ||
    data.databaseTableVariableName;
	if (data.loopType === "variable" || data.loopType === "table") {
		return data.loopType;
	}
	return tableLabel ? "table" : "variable";
}

// ─── Sentinel node data shapes ───────────────────────────────────────────────
export interface ForEachSentinelNodeData extends Record<string, unknown> {
  type: "loop-start-sentinel";
  loopName: string;
  tableName?: string;
  /** Drives Start sentinel handles (e.g. hide after-last exit for variable iteration). */
  iterateType?: "variable" | "table";
}

export interface AfterLastSentinelNodeData extends Record<string, unknown> {
  type: "loop-end-sentinel";
}

export interface ContinueSentinelNodeData extends Record<string, unknown> {
  type: "loop-continue-sentinel";
}

export type ForEachSentinelRuleNode = Node<ForEachSentinelNodeData, "loop-start-sentinel">;
export type AfterLastSentinelRuleNode = Node<AfterLastSentinelNodeData, "loop-end-sentinel">;
export type ContinueSentinelRuleNode = Node<ContinueSentinelNodeData, "loop-continue-sentinel">;

/** Union of all nodes that can appear in the loop sub-canvas. */
export type SubCanvasNode = RuleNode | ForEachSentinelRuleNode | AfterLastSentinelRuleNode | ContinueSentinelRuleNode;
export type SubCanvasEdge = RuleEdge;

// ─── Helpers ─────────────────────────────────────────────────────────────────
export const isSentinelId = (id: string): boolean =>
	id === FOREACH_SENTINEL_ID || id === AFTER_LAST_SENTINEL_ID || id === CONTINUE_SENTINEL_ID;

/**
 * Builds the full set of nodes to display in the sub-canvas by injecting
 * the ForEach and AfterLast sentinel nodes alongside the stored user nodes.
 */
export function buildSubCanvasNodes(
	storedNodes: RuleNode[],
	loopData: LoopNodeData,
): SubCanvasNode[] {
	const forEachSentinel: ForEachSentinelRuleNode = {
		id: FOREACH_SENTINEL_ID,
		type: "loop-start-sentinel",
		position: loopData.startSentinelPosition ?? FOREACH_SENTINEL_POSITION,
		data: {
			type: "loop-start-sentinel",
			loopName: loopData.name ?? "Loop",
			tableName: loopData.databaseTableName ?? loopData.screenTableName ?? loopData.tableName ?? loopData.databaseTableVariableName,
			iterateType: resolveLoopIterateType(loopData),
		},
		deletable: false,
	};

	const continueSentinel: ContinueSentinelRuleNode = {
		id: CONTINUE_SENTINEL_ID,
		type: "loop-continue-sentinel",
		position: loopData.continueSentinelPosition ?? CONTINUE_SENTINEL_POSITION,
		data: { type: "loop-continue-sentinel" },
		deletable: false,
	};

	const afterLastSentinel: AfterLastSentinelRuleNode = {
		id: AFTER_LAST_SENTINEL_ID,
		type: "loop-end-sentinel",
		position: loopData.endSentinelPosition ?? AFTER_LAST_SENTINEL_POSITION,
		data: { type: "loop-end-sentinel" },
		deletable: false,
	};
	return [forEachSentinel, ...storedNodes, continueSentinel, afterLastSentinel];
}

/**
 * Builds the full set of edges for the sub-canvas.
 * - Body: persisted `loopEdges` when non-empty; else linear chain from `storedNodes` order.
 * - Injects: Continue→Start; table loops also Start→End ("after last"). Empty body: Start→Continue ("For Each"). No Start self-loop.
 */
const DEFAULT_MARKER_END = {
	type: MarkerType.Arrow,
	color: "#b3b5b9",
	height: 28,
	width: 28,
} as const;

function normalizeUserLoopEdge(e: RuleEdge): RuleEdge {
	let next = e;
	if (e.source === FOREACH_SENTINEL_ID && e.sourceHandle === "out") {
		next = { ...e, sourceHandle: "loop-start" };
	}
	/** Rehydrate: Start→first body hop persists as `when:forEach` but edge type often `default` — needs `loop-start` for For Each pill. */
	const entryFromLoopStartIntoBody =
    next.source === FOREACH_SENTINEL_ID &&
    next.target !== CONTINUE_SENTINEL_ID &&
    next.target !== AFTER_LAST_SENTINEL_ID &&
    next.target !== FOREACH_SENTINEL_ID;
	if (entryFromLoopStartIntoBody) {
		const sh = next.sourceHandle;
		if (sh === "out" || sh == null || sh === "") {
			next = { ...next, sourceHandle: "loop-start" };
		}
		if (next.sourceHandle === "loop-start" && next.type !== "loop-start") {
			next = { ...next, type: "loop-start" };
		}
	}
	return refineForEachReturnEdgeType(next);
}

/**
 * Any non–Loop-Start source connecting to the Continue sentinel `in` uses the return arc path.
 * Decision branches keep their typed edge so pills render; otherwise the edge becomes
 * `loop-for-each-return`. If it no longer targets Continue, a stale return type falls back to `default`.
 */
export function refineForEachReturnEdgeType(e: RuleEdge): RuleEdge {
	const toContinueIn =
    e.target === CONTINUE_SENTINEL_ID &&
    (e.targetHandle === "in" || e.targetHandle == null) &&
    e.source !== CONTINUE_SENTINEL_ID &&
    e.source !== FOREACH_SENTINEL_ID;

	if (toContinueIn) {
		// Keep decision branch typing so true/false pills still render on the return arc.
		if (e.sourceHandle === "true" || e.type === "decision-true") {
			return { ...e, type: "decision-true" };
		}
		if (e.sourceHandle === "false" || e.type === "decision-false") {
			return { ...e, type: "decision-false" };
		}
		if (e.type === "loop-for-each-return") {
			return e;
		}
		let next: RuleEdge = { ...e, type: "loop-for-each-return" };
		if (e.type === "loop-end" || e.sourceHandle === "loop-end") {
			next = { ...next, sourceHandle: "out" };
		}
		return next;
	}

	if (e.type === "loop-for-each-return") {
		return { ...e, type: "default" };
	}

	return e;
}

export function createLoopStartToEndEdge(): RuleEdge {
	return {
		id: LOOP_START_TO_END_EDGE_ID,
		source: FOREACH_SENTINEL_ID,
		target: AFTER_LAST_SENTINEL_ID,
		sourceHandle: "afterLast-out",
		targetHandle: "in",
		type: "loop-end",
		deletable: false,
		reconnectable: false,
		markerEnd: DEFAULT_MARKER_END,
	};
}

export function createContinueToStartEdge(): RuleEdge {
	return {
		id: LOOP_CONTINUE_TO_START_EDGE_ID,
		source: CONTINUE_SENTINEL_ID,
		target: FOREACH_SENTINEL_ID,
		sourceHandle: "out",
		targetHandle: "in",
		type: "loop-continue-to-start",
		deletable: false,
		reconnectable: false,
		markerEnd: DEFAULT_MARKER_END,
		data: { disableAddButton: true },
	};
}

export function createLoopStartToContinueEdge(): RuleEdge {
	return {
		id: LOOP_START_TO_CONTINUE_EDGE_ID,
		source: FOREACH_SENTINEL_ID,
		target: CONTINUE_SENTINEL_ID,
		sourceHandle: "loop-start",
		targetHandle: "in",
		type: "loop-start",
		deletable: false,
		reconnectable: false,
		markerEnd: DEFAULT_MARKER_END,
		data: { addButtonBetweenPillAndMarker: true },
	};
}

function buildLoopStructuralEdges(iterateType: "variable" | "table"): RuleEdge[] {
	const out: RuleEdge[] = [createContinueToStartEdge()];
	if (iterateType !== "variable") {
		out.push(createLoopStartToEndEdge());
	}
	return out;
}

/** Default outgoing handle + edge type for loop-body nodes (ordered chain). */
function bodyOutgoingMeta(node: RuleNode): { sourceHandle: string; type: RuleEdgeType } {
	if (node.type === "decision") return { sourceHandle: "true", type: "decision-true" };
	if (node.type === "loop") return { sourceHandle: "loop-end", type: "loop-end" };
	return { sourceHandle: "out", type: "default" };
}

function isStructuralLoopSubCanvasEdge(
	e: Pick<RuleEdge, "id" | "source" | "sourceHandle" | "reconnectable" | "deletable">,
): boolean {
	if (e.reconnectable === false || e.deletable === false) return true;
	const id = e.id ?? "";
	if (
		id === LOOP_START_TO_END_EDGE_ID ||
		id === LOOP_START_TO_CONTINUE_EDGE_ID ||
		id === LOOP_CONTINUE_TO_START_EDGE_ID ||
		id === "__loop-sub-foreach-entry__"
	) {
		return true;
	}
	if (e.source === CONTINUE_SENTINEL_ID) return true;
	if (
		e.source === FOREACH_SENTINEL_ID &&
		(e.sourceHandle === "loop-start" || e.sourceHandle === "afterLast-out")
	) {
		return true;
	}
	return false;
}

/** Loop body edges (not structural): target may reconnect onto Continue / After Last. */
export function reconnectableForLoopBodyEdge(
	e: Pick<RuleEdge, "id" | "source" | "sourceHandle" | "type" | "reconnectable" | "deletable">,
): HandleType | undefined {
	if (isStructuralLoopSubCanvasEdge(e)) return undefined;
	const recon = reconnectableForEdgeType((e.type ?? "default") as RuleEdgeType);
	if (recon) return recon;
	if (e.type === "default" || e.type === "loop-for-each-return" || e.type === "loop-end") {
		return "target";
	}
	return undefined;
}

/** Shared connect + reconnect rules for the loop sub-canvas. */
export function buildLoopSubCanvasEdgeFromConnection(
	params: Connection,
	base?: Pick<RuleEdge, "id" | "markerEnd">,
): RuleEdge | null {
	const targetIsForeachInFromBody =
		params.target === FOREACH_SENTINEL_ID &&
		(params.targetHandle === "in" || params.targetHandle == null) &&
		params.source !== FOREACH_SENTINEL_ID &&
		params.source !== CONTINUE_SENTINEL_ID;

	let effective: Connection = targetIsForeachInFromBody
		? { ...params, target: CONTINUE_SENTINEL_ID, targetHandle: "in" }
		: params;

	const targetsSentinel =
		effective.target === AFTER_LAST_SENTINEL_ID || effective.target === CONTINUE_SENTINEL_ID;
	const fromStructuralStartToEnd =
		effective.source === FOREACH_SENTINEL_ID &&
		effective.sourceHandle === "afterLast-out" &&
		effective.target === AFTER_LAST_SENTINEL_ID;
	if (
		targetsSentinel &&
		(effective.source === CONTINUE_SENTINEL_ID ||
			(effective.source === FOREACH_SENTINEL_ID && !fromStructuralStartToEnd))
	) {
		return null;
	}

	let edgeType: RuleEdgeType = "default";
	if (effective.sourceHandle === "true") edgeType = "decision-true";
	if (effective.sourceHandle === "false") edgeType = "decision-false";
	if (effective.sourceHandle === "loop-start") edgeType = "loop-start";
	if (effective.sourceHandle === "afterLast-out") edgeType = "loop-end";

	const toContinueIn =
		effective.target === CONTINUE_SENTINEL_ID &&
		(effective.targetHandle === "in" || effective.targetHandle == null) &&
		effective.source !== FOREACH_SENTINEL_ID &&
		effective.source !== CONTINUE_SENTINEL_ID;
	if (toContinueIn && edgeType === "default") {
		edgeType = "loop-for-each-return";
	}

	// Ghost placeholders (e.g. decision `false` branch) have a single id-less
	// target handle, so an explicit `null` targetHandle must be preserved —
	// coercing it to "in" would point the edge at a handle the ghost lacks,
	// causing React Flow to silently drop the edge.
	const resolvedTargetHandle =
		effective.targetHandle === null ? null : (effective.targetHandle ?? "in");

	const draft: RuleEdge = {
		...effective,
		id: base?.id ?? `edge__${effective.source}-${effective.sourceHandle ?? "out"}-${effective.target}`,
		type: edgeType,
		targetHandle: resolvedTargetHandle,
		markerEnd: base?.markerEnd ?? DEFAULT_MARKER_END,
	};

	const recon = reconnectableForLoopBodyEdge(draft);
	return normalizeBodyEdgeToLoopEndSentinel(
		refineForEachReturnEdgeType({
			...draft,
			...(recon ? { reconnectable: recon } : {}),
		}),
	);
}

/**
 * Loop End target does not change edge type — source handle does (`true` / `false` / `out`).
 * Only the structural Start `afterLast-out` → End edge stays `loop-end`.
 */
function normalizeBodyEdgeToLoopEndSentinel(e: RuleEdge): RuleEdge {
	if (e.target !== AFTER_LAST_SENTINEL_ID) return e;

	if (e.source === FOREACH_SENTINEL_ID && e.sourceHandle === "afterLast-out") {
		return { ...e, type: "loop-end", sourceHandle: "afterLast-out" };
	}

	let sourceHandle = e.sourceHandle ?? "out";
	if (sourceHandle === "loop-end") sourceHandle = "out";

	if (sourceHandle === "true") {
		return { ...e, type: "decision-true", sourceHandle: "true" };
	}
	if (sourceHandle === "false") {
		return { ...e, type: "decision-false", sourceHandle: "false" };
	}

	return { ...e, type: "default", sourceHandle: "out" };
}

function finalizeSubCanvasBodyEdge(e: RuleEdge): RuleEdge {
	const refined = normalizeBodyEdgeToLoopEndSentinel(
		refineForEachReturnEdgeType(normalizeUserLoopEdge(e)),
	);
	const recon = reconnectableForLoopBodyEdge(refined);
	return recon ? { ...refined, reconnectable: recon } : refined;
}

function filterPersistedLoopBodyEdges(storedEdges: RuleEdge[], storedNodes: RuleNode[]): RuleEdge[] {
	const nodeIds = new Set(storedNodes.map((n) => n.id));
	const allowed = new Set<string>([FOREACH_SENTINEL_ID, AFTER_LAST_SENTINEL_ID, CONTINUE_SENTINEL_ID, ...nodeIds]);
	return storedEdges.filter(
		(e) =>
			e.id !== LOOP_START_TO_END_EDGE_ID &&
      e.id !== LOOP_START_TO_CONTINUE_EDGE_ID &&
      e.id !== LOOP_CONTINUE_TO_START_EDGE_ID &&
      allowed.has(e.source) &&
      allowed.has(e.target),
	);
}

/** Handle on `last` for the iteration-return edge (Loop Start `in`); nested loops use `out`, not `loop-end`. */
function sourceHandleForLoopReturnEdge(last: RuleNode): string {
	if (last.type === "decision") return "true";
	if (last.type === "loop") return "out";
	return "out";
}

/** Body node ids with no outgoing edge to another body node (DAG tails); tie-break: last in `storedNodes` order. */
function tailNodeIdForLoopReturnFromBody(bodyEdges: RuleEdge[], storedNodes: RuleNode[]): string {
	const nonGhost = storedNodes.filter((n) => n.type !== "ghost");
	const fallbackId = (nonGhost.length > 0 ? nonGhost[nonGhost.length - 1]! : storedNodes[storedNodes.length - 1]!).id;
	const nodeIds = new Set(storedNodes.filter((n) => n.type !== "ghost").map((n) => n.id));
	const hasOutgoingToBody = new Set<string>();
	for (const e of bodyEdges) {
		if (nodeIds.has(e.source) && nodeIds.has(e.target)) {
			hasOutgoingToBody.add(e.source);
		}
	}
	const sinksInStoredOrder = storedNodes
		.filter((n) => n.type !== "ghost")
		.map((n) => n.id)
		.filter((id) => !hasOutgoingToBody.has(id));
	if (sinksInStoredOrder.length === 0) return fallbackId;
	return sinksInStoredOrder[sinksInStoredOrder.length - 1]!;
}

/** Any `true` handle edge from this decision (user may point at ghost instead of Loop Start). */
function decisionHasOutgoingTrue(bodyEdges: RuleEdge[], decisionId: string): boolean {
	return bodyEdges.some((e) => e.source === decisionId && e.sourceHandle === "true");
}

/** Pass-through prep for loop sub-canvas; persisted loopNodes/loopEdges are authoritative. */
export function prepareLoopSubCanvasInitialGraph(
	storedNodes: RuleNode[],
	storedEdges: RuleEdge[],
): { nodes: RuleNode[]; edges: RuleEdge[] } {
	return { nodes: storedNodes, edges: storedEdges };
}

function buildSyntheticChainBodyEdges(storedNodes: RuleNode[]): RuleEdge[] {
	/** Ghost placeholders (e.g. decision `false`) are not part of the linear iterate chain. */
	const chainNodes = storedNodes.filter((n) => n.type !== "ghost");
	if (chainNodes.length === 0) {
		return [];
	}

	const first = chainNodes[0]!;
	const bodyEdges: RuleEdge[] = [];

	const entry: RuleEdge = {
		id: "__loop-sub-foreach-entry__",
		source: FOREACH_SENTINEL_ID,
		target: first.id,
		sourceHandle: "loop-start",
		targetHandle: "in",
		type: "loop-start",
		deletable: false,
		reconnectable: false,
		markerEnd: DEFAULT_MARKER_END,
	};
	bodyEdges.push(entry);

	for (let i = 0; i < chainNodes.length - 1; i++) {
		const from = chainNodes[i]!;
		const to = chainNodes[i + 1]!;
		let { sourceHandle, type } = bodyOutgoingMeta(from);
		sourceHandle = sourceHandle === "loop-end" ? "out" : sourceHandle;
		const reconnectable = reconnectableForEdgeType(type);
		bodyEdges.push({
			id: `__loop-sub-chain-${i}__`,
			source: from.id,
			target: to.id,
			sourceHandle,
			targetHandle: "in",
			type: type === "loop-end" ? "default" : type,
			markerEnd: DEFAULT_MARKER_END,
			...(reconnectable !== undefined ? { reconnectable } : {}),
		});
	}

	const tailId = tailNodeIdForLoopReturnFromBody(bodyEdges, chainNodes);
	const last = chainNodes.find((n) => n.id === tailId) ?? chainNodes[chainNodes.length - 1]!;
	const returnType = last.type === "decision" ? "decision-true" : "loop-for-each-return";
	const reconnectable = reconnectableForEdgeType(returnType);
	bodyEdges.push({
		id: "__loop-sub-foreach-return__",
		source: last.id,
		target: CONTINUE_SENTINEL_ID,
		sourceHandle: sourceHandleForLoopReturnEdge(last),
		targetHandle: "in",
		type: returnType,
		markerEnd: DEFAULT_MARKER_END,
		...(reconnectable !== undefined ? { reconnectable } : {}),
	});

	return bodyEdges;
}

/**
 * Builds body edges: uses persisted `storedEdges` when present (branching, order from API).
 * Otherwise falls back to a linear chain in `storedNodes` order (Loop Start → … → return).
 * Appends synthetic Continue→Start; table loops also get Start→End (after last).
 */
export function buildSubCanvasEdges(
	storedEdges: RuleEdge[],
	storedNodes: RuleNode[],
	iterateType: "variable" | "table" = "table",
): SubCanvasEdge[] {
	const structuralEdges = buildLoopStructuralEdges(iterateType);
	const continueToStartEdge = structuralEdges[0]!;
	const startToEndEdge = iterateType !== "variable" ? structuralEdges[1] : undefined;

	if (storedNodes.length === 0) {
		return [createLoopStartToContinueEdge(), ...structuralEdges];
	}

	const persisted = filterPersistedLoopBodyEdges(storedEdges, storedNodes);
	const nodeIds = new Set(storedNodes.map((n) => n.id));
	const persistedTouchesSentinel = persisted.some(
		(e) =>
			e.source === FOREACH_SENTINEL_ID ||
      e.target === FOREACH_SENTINEL_ID ||
      e.source === CONTINUE_SENTINEL_ID ||
      e.target === CONTINUE_SENTINEL_ID ||
      e.source === AFTER_LAST_SENTINEL_ID ||
      e.target === AFTER_LAST_SENTINEL_ID,
	);
	const usePersistedBody = persisted.length > 0 && persistedTouchesSentinel;

	let rawBody: RuleEdge[] = usePersistedBody
		? persisted.map((e) => ({ ...e, markerEnd: e.markerEnd ?? DEFAULT_MARKER_END }))
		: buildSyntheticChainBodyEdges(storedNodes);

	if (!usePersistedBody && persisted.length > 0) {
		const seen = new Set(rawBody.map((e) => e.id));
		for (const e of persisted) {
			if (seen.has(e.id)) continue;
			seen.add(e.id);
			rawBody.push({ ...e, markerEnd: e.markerEnd ?? DEFAULT_MARKER_END });
		}
	}

	if (usePersistedBody) {
		const hasReturnToIterationHub = rawBody.some((e) => {
			if (!nodeIds.has(e.source) || e.source === FOREACH_SENTINEL_ID || e.source === CONTINUE_SENTINEL_ID) {
				return false;
			}
			const toContinue =
        e.target === CONTINUE_SENTINEL_ID && (e.targetHandle === "in" || e.targetHandle == null);
			const toForeachLegacy =
        e.target === FOREACH_SENTINEL_ID && (e.targetHandle === "in" || e.targetHandle == null);
			return toContinue || toForeachLegacy;
		});
		if (!hasReturnToIterationHub) {
			const tailId = tailNodeIdForLoopReturnFromBody(rawBody, storedNodes);
			const last = storedNodes.find((n) => n.id === tailId) ?? storedNodes[storedNodes.length - 1]!;
			const skipAutoReturn =
        last.type === "decision" && decisionHasOutgoingTrue(rawBody, tailId);
			if (!skipAutoReturn) {
				const returnType = last.type === "decision" ? "decision-true" : "loop-for-each-return";
				const reconnectable = reconnectableForEdgeType(returnType);
				rawBody = [
					...rawBody,
					{
						id: "__loop-sub-foreach-return__",
						source: last.id,
						target: CONTINUE_SENTINEL_ID,
						sourceHandle: sourceHandleForLoopReturnEdge(last),
						targetHandle: "in",
						type: returnType,
						markerEnd: DEFAULT_MARKER_END,
						...(reconnectable !== undefined ? { reconnectable } : {}),
					},
				];
			}
		}
	}

	const finalized = rawBody.map(finalizeSubCanvasBodyEdge);
	if (startToEndEdge) {
		return [...finalized, continueToStartEdge, startToEndEdge];
	}
	return [...finalized, continueToStartEdge];
}

/**
 * Extracts the persistable nodes, edges, and sentinel positions from the sub-canvas state.
 * Filters out sentinel nodes and the loop-back edge; sentinel positions are returned separately.
 */
export function extractPersistableState(
	subNodes: SubCanvasNode[],
	subEdges: SubCanvasEdge[],
	owningLoopId?: string,
): {
  nodes: RuleNode[];
  edges: RuleEdge[];
  forEachPosition: { x: number; y: number } | undefined;
  afterLastPosition: { x: number; y: number } | undefined;
  continueNodePosX: number | undefined;
  continueNodePosY: number | undefined;
} {
	const nodes = subNodes.filter(
		(n): n is RuleNode => !isSentinelId(n.id),
	);
	const edges = subEdges.filter(
		(e) =>
			e.id !== LOOP_START_TO_END_EDGE_ID &&
      e.id !== LOOP_START_TO_CONTINUE_EDGE_ID &&
      e.id !== LOOP_CONTINUE_TO_START_EDGE_ID,
	);
	const forEachNode = subNodes.find(n => n.id === FOREACH_SENTINEL_ID);
	const afterLastNode = subNodes.find(n => n.id === AFTER_LAST_SENTINEL_ID);
	const continueNode = subNodes.find(n => n.id === CONTINUE_SENTINEL_ID);

	if (!owningLoopId) {
		return {
			nodes,
			edges,
			forEachPosition: forEachNode?.position,
			afterLastPosition: afterLastNode?.position,
			continueNodePosX: continueNode?.position.x,
			continueNodePosY: continueNode?.position.y,
		};
	}

	const scopedNodes = nodes.map(
		(n) =>
			({
				...n,
				loopNodeId: owningLoopId,
				data: { ...n.data, loopNodeId: owningLoopId },
			}) as RuleNode,
	);
	const scopedEdges = edges.map((e) => ({
		...e,
		data: { ...e.data, loopNodeId: owningLoopId },
	}));

	return {
		nodes: scopedNodes,
		edges: scopedEdges,
		forEachPosition: forEachNode?.position,
		afterLastPosition: afterLastNode?.position,
		continueNodePosX: continueNode?.position.x,
		continueNodePosY: continueNode?.position.y,
	};
}

/**
 * Nodes on the active React Flow surface: root flow nodes, or the innermost loop's `loopNodes`
 * when viewing a loop sub-canvas (nested `loopStack` supported).
 */
export function getActiveCanvasScopeNodes(
	nodes: RuleNode[],
	loopStack: LoopStackEntry[],
): RuleNode[] {
	if (loopStack.length === 0) {
		return nodes;
	}
	const data = getLoopNodeData(nodes, loopStack);
	return data?.loopNodes ?? [];
}

/**
 * Depth-first list of every rule node, recursing into each loop's `loopNodes`.
 * Used for variable orphan cleanup and deriving toolbox entries from the full graph.
 */
export function collectAllRuleNodesInTree(rootNodes: RuleNode[]): RuleNode[] {
	const out: RuleNode[] = [];
	for (const n of rootNodes) {
		out.push(n);
		if (n.type === "loop") {
			const data = n.data as LoopNodeData;
			out.push(...collectAllRuleNodesInTree(data.loopNodes ?? []));
		}
	}
	return out;
}

/**
 * Recursively reads loop node data by traversing the loop stack through the node tree.
 */
export function getLoopNodeData(
	nodes: RuleNode[],
	loopStack: LoopStackEntry[],
): { loopNodes: RuleNode[]; loopEdges: RuleEdge[]; loopNodeData: LoopNodeData } | null {
	if (loopStack.length === 0) return null;

	const [head, ...tail] = loopStack;
	const loopNode = nodes.find(n => n.id === head.loopNodeId && n.type === "loop");
	if (!loopNode) return null;

	const data = loopNode.data as LoopNodeData;

	if (tail.length === 0) {
		return {
			loopNodes: data.loopNodes ?? [],
			loopEdges: data.loopEdges ?? [],
			loopNodeData: data,
		};
	}

	return getLoopNodeData(data.loopNodes ?? [], tail);
}

/**
 * Recursively updates the loop node's loopNodes/loopEdges (and sentinel positions) at the given stack depth.
 */
export function updateLoopNodeData(
	nodes: RuleNode[],
	loopStack: LoopStackEntry[],
	newLoopNodes: RuleNode[],
	newLoopEdges: RuleEdge[],
	forEachPosition?: { x: number; y: number },
	afterLastPosition?: { x: number; y: number },
	continueSentinelPosition?: { x: number; y: number },
): RuleNode[] {
	if (loopStack.length === 0) return nodes;

	const [head, ...tail] = loopStack;

	return nodes.map(n => {
		if (n.id !== head.loopNodeId || n.type !== "loop") return n;

		if (tail.length === 0) {
			return {
				...n,
				data: {
					...n.data,
					loopNodes: newLoopNodes,
					loopEdges: newLoopEdges,
					...(forEachPosition !== undefined ? { startSentinelPosition: forEachPosition } : {}),
					...(afterLastPosition !== undefined ? { endSentinelPosition: afterLastPosition } : {}),
					...(continueSentinelPosition !== undefined ? { continueSentinelPosition: continueSentinelPosition } : {}),
				},
			};
		}

		const data = n.data as LoopNodeData;
		const updatedChildren = updateLoopNodeData(
			data.loopNodes ?? [],
			tail,
			newLoopNodes,
			newLoopEdges,
			forEachPosition,
			afterLastPosition,
			continueSentinelPosition,
		);
		return { ...n, data: { ...n.data, loopNodes: updatedChildren } };
	});
}
